import * as React from 'react';

// Base interface for filterable users
export interface UserFilterable {
	activationStatus?: string;
	email?: string;
}

// Constants for type safety
export const USER_STATUS_OPTIONS = ['ACTIVE', 'CREATED', 'DEACTIVATED'] as const;
export const USER_STATUS_LABELS: Record<string, string> = {
	ACTIVE: 'Active',
	CREATED: 'Created',
	DEACTIVATED: 'Deactivated',
};

// Map from display status to API status
export const DISPLAY_TO_API_STATUS: Record<string, string> = {
	ACTIVE: 'ACTIVE',
	CREATED: 'INIT',
	DEACTIVATED: 'DEACTIVATED',
};

export const ADMIN_USER_ROLE_OPTIONS = ['superAdmin', 'admin', 'user', 'limitedUser'] as const;
export const END_USER_ROLE_OPTIONS = ['admin', 'employee'] as const;

export const ADMIN_USER_ROLE_LABELS: Record<string, string> = {
	admin: 'Administrator',
	limitedUser: 'Limited User',
	superAdmin: 'Super Administrator',
	user: 'User',
};

export const END_USER_ROLE_LABELS: Record<string, string> = {
	admin: 'Administrator',
	employee: 'Employee',
};

/**
 * Interface for user filter state
 */
export interface UserFilters {
	/** Array of selected status filters */
	statuses: string[];
	/** Array of selected role filters */
	roles: string[];
	/** Text to search for in user names and emails */
	searchText: string;
}

/**
 * Configuration options for the useUserFilters hook
 * @template T - Type of user object that extends UserFilterable
 */
interface UseUserFiltersOptions<T extends UserFilterable> {
	/** Available role options for the role filter dropdown */
	roleOptions: readonly string[];
	/** Display labels for role options */
	roleLabels: Record<string, string>;
	/** Function to extract the role from a user object */
	getUserRole: (user: T) => string;
	/** Function to get the display name from a user object */
	getDisplayName: (user: T) => string;
}

/**
 * Custom hook for managing user filtering state and logic
 *
 * Provides comprehensive filtering functionality including:
 * - Multi-select status filtering (ACTIVE, CREATED, DEACTIVATED)
 * - Multi-select role filtering (configurable based on user type)
 * - Text search across user names and emails
 * - Smart initialization that preserves original behavior until user interaction
 *
 * @template T - Type of user object that extends UserFilterable (must have activationStatus and email)
 * @param options - Configuration options for the hook
 * @returns Object containing filter state, handlers, and utility functions
 *
 * @example
 * ```typescript
 * const userFilters = useUserFilters<Api.IUser>({
 *   roleOptions: ['admin', 'employee'],
 *   roleLabels: { admin: 'Administrator', employee: 'Employee' },
 *   getUserRole: (user) => user.groups?.includes('admin') ? 'admin' : 'employee',
 *   getDisplayName: (user) => getDisplayName(user),
 * });
 *
 * const filteredUsers = userFilters.filterUsers(allUsers);
 * ```
 */
export const useUserFilters = <T extends UserFilterable>({
	roleOptions,
	roleLabels,
	getUserRole,
	getDisplayName,
}: UseUserFiltersOptions<T>) => {
	const [filters, setFilters] = React.useState<UserFilters>({
		statuses: [...USER_STATUS_OPTIONS], // Start with all statuses selected
		roles: [...roleOptions], // Start with all roles selected
		searchText: '',
	});
	const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

	const handleStatusFilterChange = React.useCallback((status: string) => {
		setHasUserInteracted(true);
		setFilters(prev => ({
			...prev,
			statuses: prev.statuses.includes(status) ? prev.statuses.filter(s => s !== status) : [...prev.statuses, status],
		}));
	}, []);

	const handleRoleFilterChange = React.useCallback((role: string) => {
		setHasUserInteracted(true);
		setFilters(prev => ({
			...prev,
			roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
		}));
	}, []);

	const handleSearchTextChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value) {
			setHasUserInteracted(true);
		}
		setFilters(prev => ({
			...prev,
			searchText: e.target.value,
		}));
	}, []);

	const clearSearchText = React.useCallback(() => {
		setFilters(prev => ({ ...prev, searchText: '' }));
	}, []);

	const resetStatusFilter = React.useCallback(() => {
		setFilters(prev => ({ ...prev, statuses: [...USER_STATUS_OPTIONS] }));
	}, []);

	const resetRoleFilter = React.useCallback(() => {
		setFilters(prev => ({ ...prev, roles: [...roleOptions] }));
	}, [roleOptions]);

	const statusDropdownMessage = React.useMemo(() => {
		if (filters.statuses.length === 0 || filters.statuses.length === USER_STATUS_OPTIONS.length) {
			return 'All Statuses';
		}
		if (filters.statuses.length === 1) {
			return USER_STATUS_LABELS[filters.statuses[0]];
		}
		return `${USER_STATUS_LABELS[filters.statuses[0]]} or ${filters.statuses.length - 1} others`;
	}, [filters.statuses]);

	const roleDropdownMessage = React.useMemo(() => {
		if (filters.roles.length === 0 || filters.roles.length === roleOptions.length) {
			return 'All Roles';
		}
		if (filters.roles.length === 1) {
			return roleLabels[filters.roles[0]];
		}
		return `${roleLabels[filters.roles[0]]} or ${filters.roles.length - 1} others`;
	}, [filters.roles, roleOptions.length, roleLabels]);

	const filterUsers = React.useCallback(
		(users: T[]) => {
			// If user hasn't interacted with filters yet, show all users (original behavior)
			if (!hasUserInteracted) {
				return users;
			}

			return users.filter(user => {
				// Check if user's actual API status matches any of the selected display statuses
				const statusMatch =
					filters.statuses.length === 0 ||
					(user.activationStatus &&
						filters.statuses.some(displayStatus => DISPLAY_TO_API_STATUS[displayStatus] === user.activationStatus));

				const userRole = getUserRole(user);
				const roleMatch = filters.roles.length === 0 || filters.roles.includes(userRole);

				const searchMatch =
					!filters.searchText ||
					getDisplayName(user).toLowerCase().includes(filters.searchText.toLowerCase()) ||
					(user.email && user.email.toLowerCase().includes(filters.searchText.toLowerCase()));

				return statusMatch && roleMatch && searchMatch;
			});
		},
		[filters, hasUserInteracted, getUserRole, getDisplayName]
	);

	return {
		filters,
		hasUserInteracted,
		handleStatusFilterChange,
		handleRoleFilterChange,
		handleSearchTextChange,
		clearSearchText,
		resetStatusFilter,
		resetRoleFilter,
		statusDropdownMessage,
		roleDropdownMessage,
		filterUsers,
	};
};
